// eslint-disable-next-line max-len
// NOTE: this is based on https://github.com/pansa-dev/ngx-cesium/blob/HEAD/projects/@pansa/ngx-cesium/src/lib/@pansa/ngx-cesium-widgets/components/circles-editor/circles-editor.component.ts

import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, Input, NgZone, OnDestroy, TemplateRef } from "@angular/core";
import { FunctionUtils, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatestWith, map, tap } from "rxjs";
import { CylinderEditUpdate } from "../../models/cylinder/cylinder-edit-update";
import { CylinderEditPoint, CylinderLabel, EditableCylinder } from "../../models/cylinder/editable-cylinder";
import { EditActions, EditModes } from "../../models/edit-actions.enum";
import { CameraService } from "../../services/camera.service";
import { CesiumMapService } from "../../services/cesium-map.service";
import { CoordinateConverter } from "../../services/coordinate-converter/coordinate-converter.service";
import { CylindersEditorService } from "../../services/entity-editors/cylinders-editor/cylinders-editor.service";
import { CylindersManagerService } from "../../services/entity-editors/cylinders-editor/cylinders-manager.service";
import { HeightHelperService, ShapeDragActions } from "../../services/height-helper.service";
import { CesiumPointerManagerService } from "../../services/pointer-manager/cesium-pointer-manager.service";

interface CylindersEditorComponentState {
    isShown: boolean;
    cylinderLabelsTemplate: TemplateRef<unknown> | null;
    cylinderLabels: Map<string, CylinderLabel[]>;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-cylinders-editor",
    templateUrl: "./cylinders-editor.component.html",
    styleUrls: ["./cylinders-editor.component.scss"],
    providers: [CylindersManagerService, HeightHelperService, LocalComponentStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CylindersEditorComponent implements OnDestroy {
    private editLabelsRenderFunctions = new Map<string, (update: CylinderEditUpdate, labels: CylinderLabel[]) => CylinderLabel[]>();

    protected readonly isShown$ = this.localStore.selectByKey("isShown");
    protected cylinderLabels$ = this.localStore.selectByKey("cylinderLabels").pipe(
        combineLatestWith(this.isShown$),
        map(([labels, isShown]) => (isShown ? [...labels.values()].flat().filter((label) => label.show && label.text) : [])),
        // TODO: DTM-5945 after switching to zone-less remove this, it's should work out of hand then
        tap(() => this.ngZone.run(FunctionUtils.noop))
    );
    protected readonly cylinderLabelsTemplate$ = this.localStore.selectByKey("cylinderLabelsTemplate");

    @Input() public set show(value: BooleanInput) {
        const isShown = coerceBooleanProperty(value);
        this.localStore.patchState({ isShown });
        if (isShown) {
            this.cylindersManager.showAll();
        } else {
            this.cylindersManager.hideAll();
        }
    }
    @Input()
    public set labelsTemplate(value: TemplateRef<unknown> | null) {
        this.localStore.patchState({ cylinderLabelsTemplate: value });
    }

    constructor(
        private readonly cylindersEditor: CylindersEditorService,
        private readonly coordinateConverter: CoordinateConverter,
        private readonly cameraService: CameraService,
        private readonly cylindersManager: CylindersManagerService,
        private readonly cesiumService: CesiumMapService,
        private readonly cesiumPointerManager: CesiumPointerManagerService,
        private readonly localStore: LocalComponentStore<CylindersEditorComponentState>,
        heightHelperService: HeightHelperService,
        private readonly ngZone: NgZone
    ) {
        this.ngZone.runOutsideAngular(() => {
            this.cylindersEditor.init(
                this.coordinateConverter,
                this.cameraService,
                this.cylindersManager,
                this.cesiumService,
                this.cesiumPointerManager,
                heightHelperService
            );

            this.startListeningToEditorUpdates();
        });

        this.localStore.setState({
            isShown: true,
            cylinderLabelsTemplate: null,
            cylinderLabels: new Map(),
        });
    }

    private startListeningToEditorUpdates() {
        this.cylindersEditor
            .onUpdate()
            .pipe(untilDestroyed(this))
            .subscribe((update) => {
                if (update.editMode === EditModes.CREATE || update.editMode === EditModes.CREATE_OR_EDIT) {
                    this.handleCreateUpdates(update);
                } else if (update.editMode === EditModes.EDIT) {
                    this.cylindersManager.get(update.id)?.markAsDynamicEntityWhileUpdate();
                    this.handleEditUpdates(update);
                }
            });
    }

    private getLabelId(cylinder: EditableCylinder, label: CylinderLabel): string {
        return `${cylinder.getId()}_${label.id}`;
    }

    public renderEditLabels(cylinder: EditableCylinder, update: CylinderEditUpdate, labels?: CylinderLabel[]) {
        update.center = cylinder.getCenter();
        update.radiusPoint = cylinder.radiusPoint?.getPosition();
        update.radius = cylinder.getRadius();
        update.topHeight = cylinder.getTopHeight();
        update.bottomHeight = cylinder.getBottomHeight();

        if (labels) {
            cylinder.labels = labels;
            this.synchronizeLabels(cylinder);

            return;
        }

        const editLabelsRenderFn = this.editLabelsRenderFunctions.get(cylinder.getId());

        if (!editLabelsRenderFn) {
            return;
        }

        cylinder.labels = editLabelsRenderFn(update, cylinder.labels);
        this.synchronizeLabels(cylinder);
    }

    private synchronizeLabels(cylinder: EditableCylinder) {
        this.localStore.patchState(({ cylinderLabels }) => ({
            cylinderLabels: new Map(cylinderLabels).set(
                cylinder.getId(),
                cylinder.labels.map((label) => ({ ...label, entityId: this.getLabelId(cylinder, label) }))
            ),
        }));
    }

    public removeEditLabels(cylinder: EditableCylinder) {
        this.localStore.patchState(({ cylinderLabels }) => {
            const updatedLabels = new Map(cylinderLabels);
            updatedLabels.delete(cylinder.getId());

            return { cylinderLabels: updatedLabels };
        });
        cylinder.labels = [];
        this.editLabelsRenderFunctions.delete(cylinder.getId());
    }

    public handleCreateUpdates(update: CylinderEditUpdate) {
        switch (update.editAction) {
            case EditActions.INIT: {
                if (update.cylinderOptions) {
                    const cylinder = this.cylindersManager.createEditableCylinder(update.id, update.cylinderOptions);
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.MOUSE_MOVE: {
                const cylinder = this.cylindersManager.get(update.id);

                if (cylinder && update.radiusPoint) {
                    cylinder.moveRadiusPoint(update.radiusPoint);
                    this.renderEditLabels(cylinder, update);
                }

                break;
            }
            case EditActions.ADD_POINT: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder && update.center) {
                    cylinder.addPoint(update.center);
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.DONE: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder) {
                    cylinder.finishCreation();
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.DISPOSE: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder) {
                    this.removeEditLabels(cylinder);
                    this.cylindersManager.dispose(update.id);
                }
                break;
            }
            case EditActions.SET_EDIT_LABELS_RENDER_CALLBACK: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder && update.labelsRenderFn) {
                    this.editLabelsRenderFunctions.set(cylinder.getId(), update.labelsRenderFn);
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.UPDATE_EDIT_LABELS: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder) {
                    this.renderEditLabels(cylinder, update, update.updateLabels);
                }
                break;
            }
            case EditActions.SET_MANUALLY: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder) {
                    this.renderEditLabels(cylinder, update, update.updateLabels);
                }
                break;
            }
            default: {
                return;
            }
        }
    }

    public handleEditUpdates(update: CylinderEditUpdate) {
        switch (update.editAction) {
            case EditActions.INIT: {
                if (update.cylinderOptions && update.center && update.radiusPoint) {
                    const cylinder = this.cylindersManager.createEditableCylinder(update.id, update.cylinderOptions);
                    cylinder.setManually(update.center.clone(), update.radiusPoint.clone(), update.topHeight, update.bottomHeight);
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.DRAG_POINT_FINISH:
            case EditActions.DRAG_POINT: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder && cylinder.enableEdit && update.endDragPosition) {
                    cylinder.moveRadiusPoint(update.endDragPosition);
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.DRAG_SHAPE_FINISH:
            case EditActions.DRAG_SHAPE: {
                const cylinder = this.cylindersManager.get(update.id);
                if (!cylinder || !cylinder.enableEdit || update.shapeDragAction === undefined) {
                    break;
                }

                if (update.editAction === EditActions.DRAG_SHAPE_FINISH && update.shapeDragAction === ShapeDragActions.MoveEntity) {
                    cylinder.endMoveShape();
                    this.renderEditLabels(cylinder, update);
                    break;
                }

                switch (update.shapeDragAction) {
                    case ShapeDragActions.MoveEntity:
                        if (update.startDragPosition && update.endDragPosition) {
                            cylinder.moveCylinder(update.startDragPosition, update.endDragPosition);
                        }
                        break;

                    case ShapeDragActions.ChangeBottomHeight:
                        if (update.bottomHeight !== undefined) {
                            cylinder.moveBottomHeight(update.bottomHeight);
                        }
                        break;

                    case ShapeDragActions.ChangeTopHeight:
                        if (update.topHeight !== undefined) {
                            cylinder.moveTopHeight(update.topHeight);
                        }
                        break;
                }

                this.renderEditLabels(cylinder, update);
                break;
            }
            case EditActions.DISABLE: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder) {
                    cylinder.enableEdit = false;
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            case EditActions.ENABLE: {
                const cylinder = this.cylindersManager.get(update.id);
                if (cylinder) {
                    cylinder.enableEdit = true;
                    this.renderEditLabels(cylinder, update);
                }
                break;
            }
            default: {
                return;
            }
        }
    }

    public ngOnDestroy(): void {
        this.cylindersManager.clear();
    }

    public getPointSize(point: CylinderEditPoint) {
        if (point.isVirtualEditPoint()) {
            return point.props.virtualPointPixelSize;
        }

        return point.isRadiusPoint ? point.props.radiusPixelSize : point.props.pixelSize;
    }

    public getPointColor(point: CylinderEditPoint) {
        return point.isRadiusPoint ? point.props.radiusColor : point.props.color;
    }

    public getPointOutlineColor(point: CylinderEditPoint) {
        return point.isRadiusPoint ? point.props.radiusOutlineColor : point.props.outlineColor;
    }

    public getPointOutlineWidth(point: CylinderEditPoint) {
        return point.isRadiusPoint ? point.props.radiusOutlineWidth : point.props.outlineWidth;
    }

    public getPointShow(point: CylinderEditPoint) {
        return point.show && (point.isVirtualEditPoint() ? point.props.showVirtual : point.props.show);
    }

    protected trackByEntityId(index: number, label: CylinderLabel) {
        return label.entityId;
    }
}
